'use client';

import type { Address } from 'viem';
import type { ChainType } from '@/constants/chain-specific';

/**
 * GeckoTerminal API Network IDs mapping
 * Map our ChainType to GeckoTerminal network identifiers
 */
const GECKO_NETWORK_MAP: Record<number, string> = {
    109: 'shibarium', // Shibarium
    1: 'eth', // Ethereum
    56: 'bsc', // BSC
    137: 'polygon', // Polygon
    43114: 'avax', // Avalanche
    // Add more as needed
};

/**
 * Pool data from GeckoTerminal API
 */
export interface GeckoPool {
    id: string;
    type: string;
    attributes: {
        address: Address;
        name: string;
        base_token_price_usd: string;
        quote_token_price_usd: string;
        base_token_price_native_currency: string;
        quote_token_price_native_currency: string;
        pool_created_at: string;
        reserve_in_usd: string;
        fdv_usd: string;
        market_cap_usd: string;
        price_change_percentage: {
            h24: string;
        };
        transactions: {
            h24: {
                buys: number;
                sells: number;
                buyers: number;
                sellers: number;
            };
        };
        volume_usd: {
            h24: string;
        };
    };
    relationships: {
        base_token: {
            data: {
                id: string;
                type: string;
            };
        };
        quote_token: {
            data: {
                id: string;
                type: string;
            };
        };
        dex: {
            data: {
                id: string;
                type: string;
            };
        };
    };
}

export interface GeckoPoolsResponse {
    data: GeckoPool[];
    links?: {
        next?: string;
    };
}

/**
 * Normalized DEX name mapping for GeckoTerminal
 * Maps our internal DEX slugs to GeckoTerminal DEX identifiers
 */
const DEX_NAME_MAP: Record<string, string> = {
    chewyswap: 'chewyswap',
    marswap: 'marswap',
    shibaswap: 'shibaswap',
    woof: 'woof',
    // Add more mappings as needed
};

/**
 * Get top pools for a specific DEX on a network
 * Results are cached for 5 minutes using Next.js unstable_cache
 */
async function fetchTopPoolsForDex(
    network: string,
    dexSlug: string,
    options: {
        sort?: 'h24_tx_count_desc' | 'h24_volume_usd_desc' | 'reserve_usd_desc';
        page?: number;
    } = {}
): Promise<GeckoPoolsResponse> {
    const { sort = 'h24_tx_count_desc', page = 1 } = options;
  
    const geckoDexName = DEX_NAME_MAP[dexSlug] || dexSlug;
    const url = `https://api.geckoterminal.com/api/v2/networks/${network}/dexes/${geckoDexName}/pools?sort=${sort}&page=${page}`;

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
            },
            cache: 'force-cache',
        });

        if (!response.ok) {
            throw new Error(`GeckoTerminal API error: ${response.status} ${response.statusText}`);
        }

        const data: GeckoPoolsResponse = await response.json();
        return data;
    } catch (error) {
        console.error(`Error fetching top pools for ${dexSlug} on ${network}:`, error);
        return { data: [] };
    }
}

/**
 * Simple in-memory cache for client-side use
 * Replaces unstable_cache which only works server-side
 */
interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

const poolsCache = new Map<string, CacheEntry<GeckoPoolsResponse>>();
const CACHE_TTL = 300000; // 5 minutes in milliseconds

function getCachedOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    cache: Map<string, CacheEntry<T>>,
    ttl: number
): Promise<T> {
    const cached = cache.get(key);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < ttl) {
        return Promise.resolve(cached.data);
    }
    
    return fetcher().then((data) => {
        cache.set(key, { data, timestamp: now });
        return data;
    });
}

/**
 * Get top pools for a specific DEX on a network (with client-side caching)
 * Results are cached for 5 minutes
 */
export async function getTopPoolsForDex(
    network: string,
    dexSlug: string,
    sort: string = 'h24_tx_count_desc'
): Promise<GeckoPoolsResponse> {
    const cacheKey = `gecko-top-pools:${network}:${dexSlug}:${sort}`;
    
    return getCachedOrFetch(
        cacheKey,
        () => fetchTopPoolsForDex(network, dexSlug, { sort: sort as any }),
        poolsCache,
        CACHE_TTL
    );
}

/**
 * Extract pool addresses from GeckoTerminal response
 */
export function extractPoolAddresses(poolsResponse: GeckoPoolsResponse): Address[] {
    return poolsResponse.data
        .map((pool) => pool.attributes.address)
        .filter((addr): addr is Address => !!addr);
}

/**
 * Get top pool addresses for a DEX (with caching)
 * This is the main function to use for getting priority pools to check
 */
export async function getTopPoolAddressesForDex(
    chainId: ChainType,
    dexSlug: string,
    options: {
        limit?: number;
        sort?: 'h24_tx_count_desc' | 'h24_volume_usd_desc' | 'reserve_usd_desc';
    } = {}
): Promise<Address[]> {
    const { limit = 100, sort = 'h24_tx_count_desc' } = options;
  
    const network = GECKO_NETWORK_MAP[chainId as number];
    if (!network) {
        console.warn(`No GeckoTerminal network mapping for chainId ${chainId}`);
        return [];
    }

    try {
        const poolsResponse = await getTopPoolsForDex(network, dexSlug, sort);
        const addresses = extractPoolAddresses(poolsResponse);
    
        // Return limited results
        return addresses.slice(0, limit);
    } catch (error) {
        console.error('Error getting top pool addresses:', error);
        return [];
    }
}

/**
 * In-memory cache for pool addresses to prevent duplicate balance checks within a single
 * loading session. This cache is intentionally NOT persisted to any storage.
 * 
 * Purpose: Prevent redundant RPC calls when the same pair address appears in multiple
 * discovery phases (e.g., a pair might be in both computed pairs AND GeckoTerminal top pools).
 * 
 * Key behavior:
 * - Fresh on every page load (no persistence)
 * - Only tracks addresses checked during THIS loading session
 * - Does NOT prevent balance checks on known pairs from previous sessions
 * 
 * For persistence of known LP pairs, use KnownPairsCache instead.
 */
export class PoolAddressCache {
    private cache: Set<Address>;

    constructor() {
        // In-memory only - no storage, fresh every page load
        this.cache = new Set();
    }

    has(address: Address): boolean {
        return this.cache.has(address.toLowerCase() as Address);
    }

    add(address: Address) {
        this.cache.add(address.toLowerCase() as Address);
    }

    addBatch(addresses: Address[]) {
        addresses.forEach((addr) => this.cache.add(addr.toLowerCase() as Address));
    }

    clear() {
        this.cache.clear();
    }

    size(): number {
        return this.cache.size;
    }

    getAll(): Address[] {
        return Array.from(this.cache);
    }
}

/**
 * Persistent cache for known LP pair addresses where the user has a balance.
 * Uses localStorage to persist across page refreshes and sessions.
 * 
 * Purpose: Store pair addresses where the user is KNOWN to have liquidity,
 * so we can check these first on subsequent loads without re-discovering them.
 * 
 * Key behavior:
 * - Persisted to localStorage (survives page refresh, new tabs, browser restart)
 * - Keyed by user address and chain ID
 * - Only stores pair addresses, not full position data
 * - Positions are validated (balance checked) on every load
 */
export class KnownPairsCache {
    private cacheKey: string;
    private pairs: Map<string, Set<Address>>; // dexSlug -> Set of pair addresses

    constructor(chainId: ChainType, userAddress: Address) {
        const userKey = userAddress.slice(0, 10).toLowerCase();
        this.cacheKey = `known-lp-pairs-${chainId}-${userKey}`;
        this.pairs = new Map();
        this.loadFromStorage();
    }

    private loadFromStorage() {
        if (typeof window === 'undefined') return;
    
        try {
            const stored = localStorage.getItem(this.cacheKey);
            if (stored) {
                const parsed = JSON.parse(stored) as Record<string, Address[]>;
                for (const [dexSlug, addresses] of Object.entries(parsed)) {
                    this.pairs.set(dexSlug, new Set(addresses.map(a => a.toLowerCase() as Address)));
                }
            }
        } catch (error) {
            console.error('[KnownPairsCache] Error loading from storage:', error);
        }
    }

    private saveToStorage() {
        if (typeof window === 'undefined') return;
    
        try {
            const data: Record<string, Address[]> = {};
            for (const [dexSlug, addresses] of this.pairs.entries()) {
                data[dexSlug] = Array.from(addresses);
            }
            localStorage.setItem(this.cacheKey, JSON.stringify(data));
        } catch (error) {
            console.error('[KnownPairsCache] Error saving to storage:', error);
        }
    }

    /**
     * Add a pair address as a known LP position
     */
    addPair(dexSlug: string, pairAddress: Address) {
        if (!this.pairs.has(dexSlug)) {
            this.pairs.set(dexSlug, new Set());
        }
        this.pairs.get(dexSlug)!.add(pairAddress.toLowerCase() as Address);
        this.saveToStorage();
    }

    /**
     * Remove a pair from known positions (e.g., when user removes liquidity)
     */
    removePair(dexSlug: string, pairAddress: Address) {
        const dexPairs = this.pairs.get(dexSlug);
        if (dexPairs) {
            dexPairs.delete(pairAddress.toLowerCase() as Address);
            if (dexPairs.size === 0) {
                this.pairs.delete(dexSlug);
            }
            this.saveToStorage();
        }
    }

    /**
     * Get all known pair addresses for a specific DEX
     */
    getPairsForDex(dexSlug: string): Address[] {
        return Array.from(this.pairs.get(dexSlug) || []);
    }

    /**
     * Get all known pair addresses across all DEXes
     */
    getAllPairs(): { dexSlug: string; pairAddress: Address }[] {
        const result: { dexSlug: string; pairAddress: Address }[] = [];
        for (const [dexSlug, addresses] of this.pairs.entries()) {
            for (const pairAddress of addresses) {
                result.push({ dexSlug, pairAddress });
            }
        }
        return result;
    }

    /**
     * Get all DEX slugs that have known pairs
     */
    getDexSlugs(): string[] {
        return Array.from(this.pairs.keys());
    }

    /**
     * Check if a pair is known
     */
    hasPair(dexSlug: string, pairAddress: Address): boolean {
        return this.pairs.get(dexSlug)?.has(pairAddress.toLowerCase() as Address) ?? false;
    }

    /**
     * Clear all known pairs
     */
    clear() {
        this.pairs.clear();
        if (typeof window !== 'undefined') {
            localStorage.removeItem(this.cacheKey);
        }
    }

    /**
     * Get total count of known pairs
     */
    size(): number {
        let count = 0;
        for (const addresses of this.pairs.values()) {
            count += addresses.size;
        }
        return count;
    }
}

/**
 * Get all top pools for multiple DEXs on a chain
 */
export async function getTopPoolsForChain(
    chainId: ChainType,
    dexSlugs: string[],
    options: {
        limit?: number;
        sort?: 'h24_tx_count_desc' | 'h24_volume_usd_desc' | 'reserve_usd_desc';
    } = {}
): Promise<Map<string, Address[]>> {
    const results = new Map<string, Address[]>();

    await Promise.all(
        dexSlugs.map(async (dexSlug) => {
            const addresses = await getTopPoolAddressesForDex(chainId, dexSlug, options);
            results.set(dexSlug, addresses);
        })
    );

    return results;
}
